/**
 * The permission-gated apps a user may open in a store, shown instantly from
 * a local cache (up to 7 days old) and re-checked with the authorizer in the
 * background. The fresh answer replaces the cache and the UI updates.
 *
 * Used by the authenticator's app launcher, every app's Applications menu
 * and `AppAccessGate`, so they all share one check and one cache.
 */
import type {
  AuthorizeOpenApp,
  AuthorizeOpenAppResult,
  Employee,
} from '@bofrak-backend/shared';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { PERMISSION_GATED_APPS } from '../constants/apps';
import type { AppInfo } from '../ui/nav/nav-bar.types';
import {
  allowedAppsCacheKey,
  readAllowedApps,
  writeAllowedApps,
} from '../utils/allowed-apps-cache';
import {
  allowedAppsStatus,
  AllowedAppsStatus,
  allowedAppUrls,
  APPS_DOMAIN,
} from './allowed-apps-state';

export interface UseAllowedAppsArgs {
  apiAdapter: {
    authorizeAppOpen(dto: AuthorizeOpenApp): Promise<AuthorizeOpenAppResult>;
  };
  employee?: Pick<Employee, 'id' | 'is_owner' | 'merchant_id'> | null;
  storeId?: string | null;
  stage: string;
}

export interface AllowedApps {
  /** Permission-gated apps the user may open (defaults like Help excluded). */
  allowedApps: AppInfo[];
  status: AllowedAppsStatus;
  /** True once the server has answered in this session. */
  isFresh: boolean;
  /** The latest server check failed. */
  isError: boolean;
  refetch: () => void;
}

const APP_URLS = PERMISSION_GATED_APPS.map((app) => app.url);

export const useAllowedApps = ({
  apiAdapter,
  employee,
  storeId,
  stage,
}: UseAllowedAppsArgs): AllowedApps => {
  const employeeId = employee?.id;
  const enabled = !!employeeId && !!storeId;
  const cacheKey = enabled
    ? allowedAppsCacheKey(stage, employeeId!, storeId!)
    : null;
  const cached = useMemo(
    () => (cacheKey ? readAllowedApps(cacheKey) : null),
    [cacheKey],
  );

  const query = useQuery(
    ['authorize-app-open', employeeId, storeId],
    async () =>
      allowedAppUrls(
        await apiAdapter.authorizeAppOpen({
          app_names: APP_URLS.map((url) => `${url}.${stage}.${APPS_DOMAIN}`),
          principal_id: employee!.is_owner
            ? employee!.merchant_id
            : employee!.id,
          store_id: storeId!,
          merchant_id: employee!.merchant_id,
        }),
        APP_URLS,
      ),
    {
      enabled,
      // Shared by the menu and the gate; one check per minute is plenty.
      staleTime: 60 * 1000,
      onSuccess: (urls) => {
        if (cacheKey) writeAllowedApps(cacheKey, urls);
      },
    },
  );

  const urls = query.data ?? cached;
  const allowedApps = useMemo(
    () => PERMISSION_GATED_APPS.filter((app) => urls?.includes(app.url)),
    [urls],
  );

  return {
    allowedApps,
    status: allowedAppsStatus({ enabled, urls, isError: query.isError }),
    isFresh: query.isSuccess,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
};
